import {
  ChatAlt2Icon,
  CheckCircleIcon,
  ClockIcon,
  CodeIcon,
  CubeIcon,
  CurrencyDollarIcon,
  CursorClickIcon,
  DatabaseIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  HashtagIcon,
  IdentificationIcon,
  LightningBoltIcon,
  LinkIcon,
  MenuAlt2Icon,
  ServerIcon,
  SwitchHorizontalIcon,
  TagIcon,
} from "@heroicons/react/outline";

import { buildStructuredSearchPayload } from "./structured-search-payload";

import { createStructuredSearchDefaultDateRange } from "@/components/StructuredSearch/stores/structured-search/structured-search-store";
import {
  ALL_AGENT_INTENTS,
  ALL_STATUSES,
  ALL_USER_INTENTS,
  ARRAY_OPERATORS,
  BOOLEAN_OPERATORS,
  getIntentDisplayLabel,
  ID_OPERATORS,
  INTENT_OPERATORS,
  MODEL_OPERATORS,
  NESTED_OPERATORS,
  NUMERIC_OPERATORS,
  PROMPT_OPERATORS,
  PROVIDER_OPERATORS,
  SIGNAL_OPERATORS,
  STATUS_OPERATORS,
  STRING_OPERATORS,
  TEXT_SEARCH_OPERATORS,
} from "@/components/StructuredSearch/config/constants";
import {
  FilterValueType,
  GlobalSearchControlType,
  SuggestionSourceType,
  ValueDisplayType,
  type FilterDefinition,
  type FilterEnumOption,
  type StructuredSearchSchema,
} from "@/components/StructuredSearch/config/structured-search-schema";
import Prompt from "@/components/ui/icons/Prompt";
import { getRequestStatusDisplayLabel } from "@/components/ui/RequestStatusBadge";

const ICON_CLASS = "bg-gray-100 text-gray-500";

const STATUS_OPTIONS: FilterEnumOption[] = ALL_STATUSES.map(status => ({
  value: status,
  label: getRequestStatusDisplayLabel(status),
}));

const USER_INTENT_OPTIONS: FilterEnumOption[] = ALL_USER_INTENTS.map(intent => ({
  value: intent,
  label: getIntentDisplayLabel(intent),
}));

const AGENT_INTENT_OPTIONS: FilterEnumOption[] = ALL_AGENT_INTENTS.map(intent => ({
  value: intent,
  label: getIntentDisplayLabel(intent),
}));

const REQUEST_FILTERS: FilterDefinition[] = [
  {
    key: "prompt_id",
    label: "Prompt",
    type: FilterValueType.Prompt,
    operators: PROMPT_OPERATORS,
    group: "Prompt",
    suggestions: { source: SuggestionSourceType.Aggregation },
    aliases: ["version", "prompt"],
    icon: Prompt,
    iconClassName: ICON_CLASS,
    description: "Used to generate the request",
  },
  {
    key: "user_id",
    label: "User ID",
    type: FilterValueType.String,
    operators: ID_OPERATORS,
    group: "User",
    suggestions: { source: SuggestionSourceType.Aggregation },
    aliases: ["user id", "userid"],
    icon: IdentificationIcon,
    iconClassName: ICON_CLASS,
    description: "User associated with the request",
  },
  {
    key: "engine",
    label: "Model",
    type: FilterValueType.String,
    operators: MODEL_OPERATORS,
    group: "Model",
    suggestions: { source: SuggestionSourceType.Aggregation },
    aliases: ["model", "engine", "llm"],
    icon: CubeIcon,
    iconClassName: ICON_CLASS,
    description: "Model that generated the response",
  },
  {
    key: "provider_type",
    label: "Provider",
    type: FilterValueType.String,
    operators: PROVIDER_OPERATORS,
    group: "Model",
    suggestions: { source: SuggestionSourceType.Aggregation },
    aliases: ["provider", "vendor"],
    valueDisplay: ValueDisplayType.Provider,
    icon: ServerIcon,
    iconClassName: ICON_CLASS,
    description: "API provider (e.g. OpenAI, Anthropic)",
  },
  {
    key: "tags",
    label: "Tags",
    type: FilterValueType.String,
    operators: ARRAY_OPERATORS,
    group: "Labels & Metadata",
    suggestions: { source: SuggestionSourceType.Aggregation },
    aliases: ["tag", "tags", "label"],
    icon: TagIcon,
    iconClassName: ICON_CLASS,
    description: "Tags attached to help organize requests",
  },
  {
    key: "metadata",
    label: "Metadata",
    type: FilterValueType.Nested,
    operators: NESTED_OPERATORS,
    group: "Labels & Metadata",
    suggestions: {
      source: SuggestionSourceType.Nested,
      keysField: "metadata_keys",
      valuesField: "metadata_values",
    },
    icon: DatabaseIcon,
    iconClassName: ICON_CLASS,
    description: "Custom metadata attached to the request",
  },
  {
    key: "signal",
    label: "Signal",
    type: FilterValueType.Nested,
    operators: SIGNAL_OPERATORS,
    group: "Labels & Metadata",
    suggestions: {
      source: SuggestionSourceType.Nested,
      keysField: "metadata_keys",
      valuesField: "metadata_values",
    },
    aliases: ["signal", "signals", "assertion"],
    icon: LightningBoltIcon,
    iconClassName: ICON_CLASS,
    description: "LLM assertion signals attached to the request",
  },
  {
    key: "output",
    label: "Output",
    type: FilterValueType.Nested,
    operators: NESTED_OPERATORS,
    group: "Content",
    suggestions: {
      source: SuggestionSourceType.Nested,
      keysField: "output_keys",
      valuesField: "output_values",
    },
    aliases: ["output", "output key", "output value", "response"],
    icon: ChatAlt2Icon,
    iconClassName: ICON_CLASS,
    description: "Structured output key-value pairs from the response",
  },
  {
    key: "input_variables",
    label: "Input Variable",
    type: FilterValueType.Nested,
    operators: NESTED_OPERATORS,
    group: "Content",
    suggestions: {
      source: SuggestionSourceType.Nested,
      keysField: "input_variable_keys",
      valuesField: "input_variable_values",
    },
    aliases: ["variable", "variables", "input variable", "input variables"],
    icon: DocumentTextIcon,
    iconClassName: ICON_CLASS,
    description: "Input variables passed to the prompt template",
  },
  {
    key: "input_text",
    label: "Input Text",
    type: FilterValueType.String,
    operators: TEXT_SEARCH_OPERATORS,
    group: "Content",
    icon: DocumentTextIcon,
    iconClassName: ICON_CLASS,
    description: "Input content sent to the model",
  },
  {
    key: "output_text",
    label: "Output Text",
    type: FilterValueType.String,
    operators: TEXT_SEARCH_OPERATORS,
    group: "Content",
    icon: ChatAlt2Icon,
    iconClassName: ICON_CLASS,
    description: "Response text returned by the model",
  },
  {
    key: "thinking_text",
    label: "Thinking Text",
    type: FilterValueType.String,
    operators: TEXT_SEARCH_OPERATORS,
    group: "Content",
    aliases: ["thinking", "reasoning", "reasoning text"],
    icon: ChatAlt2Icon,
    iconClassName: ICON_CLASS,
    description: "Reasoning or thinking text generated by the model",
  },
  {
    key: "user_intent",
    label: "User Intent",
    type: FilterValueType.Enum,
    operators: INTENT_OPERATORS,
    group: "Content",
    options: USER_INTENT_OPTIONS,
    valueDisplay: ValueDisplayType.Intent,
    aliases: [
      "sentiment",
      "mood",
      "frustrated",
      "satisfied",
      "curious",
      "user intent",
      "user mood",
    ],
    icon: IdentificationIcon,
    iconClassName: ICON_CLASS,
    description: "Lexical signals of user frustration, satisfaction, or curiosity",
  },
  {
    key: "agent_intent",
    label: "Agent Intent",
    type: FilterValueType.Enum,
    operators: INTENT_OPERATORS,
    group: "Content",
    options: AGENT_INTENT_OPTIONS,
    valueDisplay: ValueDisplayType.Intent,
    aliases: ["apologetic", "refusal", "uncertain", "agent intent", "assistant intent"],
    icon: ChatAlt2Icon,
    iconClassName: ICON_CLASS,
    description: "Lexical signals of assistant tone such as apologetic or refusal",
  },
  {
    key: "input_tokens",
    label: "Input Tokens",
    type: FilterValueType.Number,
    operators: NUMERIC_OPERATORS,
    group: "Usage",
    unit: "tokens",
    aliases: ["tokens", "input tokens"],
    icon: HashtagIcon,
    iconClassName: ICON_CLASS,
    description: "Tokens counted in the input",
  },
  {
    key: "output_tokens",
    label: "Output Tokens",
    type: FilterValueType.Number,
    operators: NUMERIC_OPERATORS,
    group: "Usage",
    unit: "tokens",
    icon: HashtagIcon,
    iconClassName: ICON_CLASS,
    description: "Tokens counted in the output",
  },
  {
    key: "cost",
    label: "Cost",
    type: FilterValueType.Cost,
    operators: NUMERIC_OPERATORS,
    group: "Usage",
    unit: "$",
    aliases: ["price", "cost", "spend"],
    icon: CurrencyDollarIcon,
    iconClassName: ICON_CLASS,
    description: "Estimated cost of the request",
  },
  {
    key: "latency_ms",
    label: "Latency",
    type: FilterValueType.Latency,
    operators: NUMERIC_OPERATORS,
    group: "Usage",
    unit: "ms",
    aliases: ["time", "duration", "latency"],
    icon: ClockIcon,
    iconClassName: ICON_CLASS,
    description: "Time it took to complete the request",
  },
  {
    key: "turn_count",
    label: "# of Turns",
    type: FilterValueType.Number,
    operators: NUMERIC_OPERATORS,
    group: "Content",
    aliases: ["turns", "assistant turns", "# of turns"],
    icon: SwitchHorizontalIcon,
    iconClassName: ICON_CLASS,
    description:
      "Number of assistant messages in the chat transcript (excluding the system message)",
  },
  {
    key: "tool_call_count",
    label: "# of Tool Calls",
    type: FilterValueType.Number,
    operators: NUMERIC_OPERATORS,
    group: "Content",
    aliases: ["tool calls", "# of tool calls", "toolcalls"],
    icon: CursorClickIcon,
    iconClassName: ICON_CLASS,
    description: "Total tool calls attached to assistant messages in the transcript",
  },
  {
    key: "status",
    label: "Request Status",
    type: FilterValueType.Enum,
    operators: STATUS_OPERATORS,
    group: "Request",
    suggestions: { source: SuggestionSourceType.Aggregation },
    options: STATUS_OPTIONS,
    valueDisplay: ValueDisplayType.RequestStatus,
    icon: CheckCircleIcon,
    iconClassName: ICON_CLASS,
    description: "Whether the request succeeded, failed, or is pending",
  },
  {
    key: "pl_id",
    label: "Request ID",
    type: FilterValueType.String,
    operators: ID_OPERATORS,
    group: "Request",
    icon: IdentificationIcon,
    iconClassName: ICON_CLASS,
    description: "Unique PromptLayer ID for the request",
  },
  {
    key: "error_type",
    label: "Error Type",
    type: FilterValueType.String,
    operators: STRING_OPERATORS,
    group: "Request",
    suggestions: { source: SuggestionSourceType.Aggregation },
    icon: ExclamationCircleIcon,
    iconClassName: ICON_CLASS,
    description: "Machine-readable error classification on failed or warned requests",
  },
  {
    key: "is_json",
    label: "Response is JSON",
    type: FilterValueType.Boolean,
    operators: BOOLEAN_OPERATORS,
    group: "Format",
    icon: CodeIcon,
    iconClassName: ICON_CLASS,
    description: "Whether the response can be parsed as valid JSON",
  },
  {
    key: "is_tool_call",
    label: "Response is a Tool Call",
    type: FilterValueType.Boolean,
    operators: BOOLEAN_OPERATORS,
    group: "Format",
    icon: CursorClickIcon,
    iconClassName: ICON_CLASS,
    description: "Whether the response includes a tool call",
  },
  {
    key: "is_plain_text",
    label: "Response is Plain Text",
    type: FilterValueType.Boolean,
    operators: BOOLEAN_OPERATORS,
    group: "Format",
    icon: MenuAlt2Icon,
    iconClassName: ICON_CLASS,
    description: "Whether the response is plain text rather than structured output",
  },
  {
    key: "has_trace",
    label: "Request is Part of Trace",
    type: FilterValueType.Boolean,
    operators: BOOLEAN_OPERATORS,
    group: "Format",
    aliases: ["trace", "part of trace", "otel"],
    icon: LinkIcon,
    iconClassName: ICON_CLASS,
    description: "Whether the request belongs to an OpenTelemetry trace",
  },
  {
    key: "tool_names",
    label: "Tool Name",
    type: FilterValueType.String,
    operators: ARRAY_OPERATORS,
    group: "Format",
    suggestions: { source: SuggestionSourceType.Aggregation },
    aliases: ["tool", "tools", "tool name", "tool names", "function"],
    icon: CursorClickIcon,
    iconClassName: ICON_CLASS,
    description: "Names of tools used in the response",
  },
];

const PLACEHOLDER = "Search requests or add filters (e.g. model, latency, tags)...";
const COMPACT_PLACEHOLDER = "Search or add filters…";

export const requestsSearchSchema: StructuredSearchSchema = {
  id: "requests",
  placeholder: PLACEHOLDER,
  compactPlaceholder: COMPACT_PLACEHOLDER,
  filters: REQUEST_FILTERS,
  globalControls: [
    { type: GlobalSearchControlType.DateRange, field: "request_start_time" },
    { type: GlobalSearchControlType.Timezone },
    { type: GlobalSearchControlType.FreeTextQuery },
    { type: GlobalSearchControlType.CutoverDate },
  ],
  showGroupHeaders: false,
  buildPayload: ({ filters, dateRange }) =>
    buildStructuredSearchPayload(filters, dateRange ?? createStructuredSearchDefaultDateRange())
      .filterGroup,
};
